import type { MeWatermark, TranscodeEffective, WatermarkDetail, WatermarkEffective } from '../api';
import { t } from '../i18n';
import { resText } from './transcode';

/*
 * WM3 (ADR-0005 W1–W10, owner's decisions 02.11.2026 on claude/wm-ui-stage0.md, AUDIT WMU-01…10):
 * what the cabinet and the console say about the free plan's Strevix mark. The center sends codes
 * (watermark_effective {on, reason, detail, since}, transcode_effective.watermark, /api/me watermark);
 * the words are made here, as transcode.ts does for transcoding.
 *
 * - The cabinet calls it "Strevix mark" / "marca de Strevix" / "знак Strevix" (WMU-01); "watermark"
 *   stays in the console and the code.
 * - The output row shows the mark only when it is really burned in on that output (WMU-03): a free
 *   broadcast without it (no GPU, a transcoder lost, the first seconds) shows nothing and warns
 *   nobody — that is our failure, the console shows it (WMU-08).
 * - The preview never shows the mark (WMU-04).
 */

/** "Strevix mark · 720p30 · Free plan": the text around the resolution, which the row sets in mono. */
export interface MarkRow {
  /** the text before and after {res}; without a resolution `after` is empty and `res` undefined */
  before: string;
  res?: string;
  after: string;
  /** the row's title: what viewers see */
  title: string;
}

/** The output row's second line (WMU-02, WMU-03); undefined when the mark is not burned in on this output. */
export function markRow(eff: TranscodeEffective | undefined): MarkRow | undefined {
  if (!eff?.watermark) return undefined;
  const title = t('wm.rowTitle');
  const out = eff.output;
  if (!out || !out.h) return { before: t('wm.rowNoRes'), after: '', title };
  const s = t('wm.row', { res: '{res}' });
  const at = s.indexOf('{res}');
  if (at < 0) return { before: s, after: '', title };
  return { before: s.slice(0, at), res: resText(out), after: s.slice(at + '{res}'.length), title };
}

/* ── the toast when the plan changes on air (WMU-05, W7) ─────────────────── */

export type MarkToastKind = 'on' | 'off';

/**
 * What the Studio remembers between two snapshots of watermark_effective. `paid` = since the mark was
 * last on, a snapshot said the plan goes without it (reason no_watermark): the next time the mark comes
 * on, the plan changed to Free on air.
 */
export interface MarkMemo {
  on: boolean;
  paid: boolean;
}

/** The first snapshot after the page loads: remembered, never a toast. */
export function markMemo(eff: WatermarkEffective | undefined): MarkMemo | undefined {
  return eff ? { on: eff.on, paid: eff.reason === 'no_watermark' } : undefined;
}

/**
 * The next snapshot. A toast only for a plan change on air:
 * - on: the mark came on after the plan had gone without it (Free now). While the job starts
 *   (node_unavailable / starting) nothing is said yet; a free broadcast whose mark never comes on
 *   (no GPU) gets no toast — the output rows do not show it either (WMU-03).
 * - off: the plan now goes without it (no_watermark) after the mark was on.
 * The mark dropping or coming back on its own (a transcoder lost, the flag) is not a plan change.
 */
export function markStep(prev: MarkMemo | undefined, eff: WatermarkEffective | undefined): { memo: MarkMemo | undefined; toast?: MarkToastKind } {
  if (!eff) return { memo: prev };
  if (!prev) return { memo: markMemo(eff) };
  const paid = eff.reason === 'no_watermark';
  if (paid) return { memo: { on: false, paid: true }, toast: prev.on ? 'off' : undefined };
  if (eff.reason === 'off') return { memo: { on: false, paid: false } };
  if (eff.on && !prev.on && prev.paid) return { memo: { on: true, paid: false }, toast: 'on' };
  return { memo: { on: eff.on, paid: eff.on ? false : prev.paid } };
}

/** The toast's words; {plan} of "removed" is the plan the account is on now. */
export function markToastText(kind: MarkToastKind, plan: string | undefined): { title: string; body: string } {
  if (kind === 'on') return { title: t('wm.toastOn.title'), body: t('wm.toastOn.body') };
  return { title: t('wm.toastOff.title'), body: t('wm.toastOff.body', { plan: plan || t('plan.yourPlan') }) };
}

/* ── Plan & usage (WMU-06) ────────────────────────────────────────────── */

/** The free plan's limit "Output to platforms · 720p30": only for a plan with the mark, and only once the flag is on. */
export function outputLimit(w: MeWatermark | undefined): string | undefined {
  return w && w.plan && w.enabled && w.output ? w.output : undefined;
}

/** While the flag is off, a plan with the mark does not list "No Strevix mark · Not on this plan": nobody has the mark yet. */
export function hideNoMarkRow(w: MeWatermark | undefined): boolean {
  return !!w && w.plan && !w.enabled;
}

/* ── console (English only, like the rest of the console; WMU-07, WMU-08, WMU-09) ── */

/** 42 → "42 s", 192 → "3 min", 4000 → "1 h 6 min". */
export function markDuration(sinceIso: string | undefined, now: number): string {
  if (!sinceIso) return '—';
  const s = Math.max(0, Math.floor((now - Date.parse(sinceIso)) / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

/** What the console knows around the stream: transcoders (online with blend / online) and the stream's watermark job. */
export interface MarkContext {
  online?: number;
  blend?: number;
  /** the transcoder of the stream's watermark job (or the last one known) */
  node?: string;
  /** the output node the copy goes out from */
  egress?: string;
}

/** Why a free broadcast goes out without the mark (detail of node_unavailable), one clause. */
export function markWhy(detail: WatermarkDetail, ctx: MarkContext = {}): string {
  switch (detail) {
    case 'no_blend_node':
      return ctx.online !== undefined ? `no transcoder can blend (${ctx.blend ?? 0} of ${ctx.online} online)` : 'no transcoder can blend';
    case 'transcoder_lost':
      return ctx.node ? `transcoder ${ctx.node} went away; moving` : 'the transcoder went away; moving';
    case 'no_relay':
      return ctx.egress ? `output node ${ctx.egress} can’t switch to the marked copy` : 'an output node can’t switch to the marked copy';
    case 'starting':
      return 'the job is starting';
  }
  return 'no transcoder makes it';
}

/** The stream card's line (Streams): on, missing, or nothing (paid plan, flag off, an older center). */
export function streamMarkLine(eff: WatermarkEffective | undefined, now: number, ctx: MarkContext = {}): { state: 'on' | 'missing'; text: string } | undefined {
  if (!eff) return undefined;
  if (eff.on) return { state: 'on', text: ctx.node ? `Strevix mark: on · ${ctx.node}` : 'Strevix mark: on' };
  if (eff.reason !== 'node_unavailable') return undefined;
  return { state: 'missing', text: `Strevix mark: missing for ${markDuration(eff.since, now)} — ${markWhy(eff.detail, ctx)}` };
}

/** One step of an entitlement's breakdown (GET /api/admin/users/{id}/billing). */
export interface FeatureStep {
  layer: string;
  op: string;
}

/** The user card's line (WMU-09): the flag, the plan, a manual grant. undefined: the center says nothing about the mark. */
export function userMarkLine(flag: MeWatermark | undefined, features: string[] | undefined, steps: FeatureStep[] | undefined): { on: boolean; text: string } | undefined {
  if (!flag || !features) return undefined;
  if (!flag.enabled) return { on: false, text: 'Strevix mark: off for everyone' };
  if (!features.includes('no_watermark')) return { on: true, text: 'Strevix mark: on outputs (Free plan)' };
  const last = steps?.[steps.length - 1];
  const granted = !!last && last.layer !== 'plan' && last.op === 'grant';
  return { on: false, text: granted ? 'Strevix mark: off (granted)' : 'Strevix mark: off (plan)' };
}

/** Network › Transcoders: the engine makes the mark. */
export const canBlend = (caps: string[] | undefined) => !!caps?.includes('blend');

/** Free broadcasts going out without the mark now (the capacity line's count). */
export function freeWithoutMark(streams: { live: boolean; watermark_effective?: WatermarkEffective }[] | undefined): number {
  return (streams ?? []).filter((s) => s.live && s.watermark_effective && !s.watermark_effective.on && s.watermark_effective.reason === 'node_unavailable').length;
}
